import React from 'react';
import { EdgeDocsCanvasV3 } from './EdgeDocsCanvas.V3';
import { TypesetterWorkerClient } from '../../workers/TypesetterWorkerClient';
import { useSharedInput } from './SharedInputContext';

/**
 * EdgeDocs Page Item - Individual page component for virtual scrolling
 * 
 * Renders a single page with canvas and page number overlay.
 * Designed to be used with react-window for efficient virtualization.
 * 
 * ✅ REFACTORED: Now uses shared input handler from SharedInputContext
 * instead of each page having its own input handler.
 */
interface EdgeDocsPageItemProps {
  /** Page index (0-based) */
  pageIndex: number;
  /** Document state */
  documentState?: { text: string; pageSetup?: any };
  /** Page width in pixels */
  pageWidth: number;
  /** Page height in pixels */
  pageHeight: number;
  /** Scale factor for zoom/DPI */
  scale: number;
  /** Page setup for layout calculations */
  pageSetup?: any;
  /** Callback for WASM initialization status changes (only page 0) */
  onWasmStatusChange?: (status: 'loading' | 'ready' | 'error') => void;
  /** Callback for document text changes (only page 0) */
  onDocumentTextChange?: (newText: string) => void;
  /** Callback for page count changes (only page 0) */
  onPageCountChange?: (pageCount: number) => void;
  /** PHASE 2.2: Shared WASM worker */
  sharedWasmClient?: TypesetterWorkerClient | null;
  /** PHASE 2.2: Skip worker initialization (use shared worker) */
  skipInitialization?: boolean;
  /** PHASE 2.2: Pre-computed page data from shared pagination */
  pageData?: any;
  /** PHASE 2.2: Full pagination result (for total_pages, etc.) */
  paginationResult?: any;
}

export const EdgeDocsPageItem: React.FC<EdgeDocsPageItemProps> = React.memo(({
  pageIndex,
  documentState,
  pageWidth,
  pageHeight,
  scale,
  pageSetup,
  onWasmStatusChange,
  onDocumentTextChange,
  onPageCountChange,
  sharedWasmClient,
  skipInitialization = false,
  pageData,
  paginationResult,
}) => {
  // ✅ Get shared input handler from context
  const sharedInputContext = useSharedInput();
  
  // PHASE 2.2: Only render canvas when shared worker is ready
  const shouldRenderCanvas = skipInitialization && sharedWasmClient;
  
  return (
    <div
      style={{
        position: 'relative',
        width: `${pageWidth * scale}px`,
        height: `${pageHeight * scale}px`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        borderRadius: '2px',
        backgroundColor: 'white',
        overflow: 'hidden',
      }}
    >
      {/* PHASE 2.2: Render canvas only when shared worker is ready */}
      {shouldRenderCanvas ? (
        <EdgeDocsCanvasV3
          documentState={documentState}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
          scale={scale}
          pageSetup={pageSetup}
          pageIndex={pageIndex}
          onWasmStatusChange={pageIndex === 0 ? onWasmStatusChange : undefined}
          onDocumentTextChange={pageIndex === 0 ? onDocumentTextChange : undefined}
          onPageCountChange={pageIndex === 0 ? onPageCountChange : undefined}
          sharedWasmClient={sharedWasmClient}
          skipInitialization={skipInitialization}
          pageData={pageData}
          paginationResult={paginationResult}
          sharedInputContext={sharedInputContext}
        />
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#999',
          fontSize: '14px'
        }}>
          Loading...
        </div>
      )}
      
      {/* Page number overlay */}
      <div
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '11px',
          color: '#666',
          fontFamily: 'system-ui',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      >
        Page {pageIndex + 1}
      </div>
    </div>
  );
});

EdgeDocsPageItem.displayName = 'EdgeDocsPageItem';
